import { Box, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { JSX } from "react";

import {
  BulkActionBar,
  BulkButton,
  BulkCheckbox,
  useBulkSelection,
} from "../../../../components/bulk-selection/index.js";
import { ConfirmDialog } from "../../../../components/ui/confirm-dialog.js";
import { PageLoader } from "@/components/ui/page-loader";
import { supportApi } from "../../client.js";
import { SupportErrorState } from "../../components/SupportEmptyState.js";
import type { SupportPriority } from "../../types.js";
import { PRIORITY_GRID } from "./grids.js";
import { NewPriorityDialog } from "./NewPriorityDialog.js";
import { PriorityRow } from "./PriorityRow.js";

/** Priorities (urgency tiers) editor: list + inline edit + create modal +
 * single / bulk delete. Extracted from `SettingsWorkflowsPanel`. */
export function PrioritiesEditor(): JSX.Element {
  const qc = useQueryClient();
  const priorityQuery = useQuery({
    queryKey: ["support", "priorities"],
    queryFn: supportApi.listPriorities,
  });
  const create = useMutation({
    mutationFn: (b: { name: string; color?: string }) =>
      supportApi.createPriority(b),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["support", "priorities"] }),
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<SupportPriority> }) =>
      supportApi.updatePriority(id, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["support", "priorities"] }),
  });
  const remove = useMutation({
    mutationFn: (id: number) => supportApi.deletePriority(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["support", "priorities"] }),
  });
  const bulkRemove = useMutation({
    mutationFn: async (ids: number[]) => {
      await Promise.allSettled(ids.map((id) => supportApi.deletePriority(id)));
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["support", "priorities"] }),
  });

  const rows = priorityQuery.data?.data ?? [];
  const bulk = useBulkSelection(rows);

  const [showCreate, setShowCreate] = useState(false);
  // Pending priority delete (drives <ConfirmDialog>).
  const [pendingDelete, setPendingDelete] = useState<{ id: number; name: string } | null>(null);
  const [confirmBulk, setConfirmBulk] = useState(false);

  return (
    <Box component="section" sx={{ display: "grid", gap: 1.5 }}>
      <Box component="header" sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }}>
        <Box>
          <Typography component="h2" sx={{ fontSize: "1.125rem", fontWeight: 600, letterSpacing: "-0.01em" }}>
            Priorities
          </Typography>
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            Urgency tiers attached to a ticket. Used by SLA policies and
            reporting.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => setShowCreate(true)}
          data-testid="workflow-add-priority"
        >
          Add priority
        </Button>
      </Box>

      <BulkActionBar count={bulk.count} onClear={bulk.clear}>
        <BulkButton
          variant="destructive"
          disabled={bulkRemove.isPending}
          onClick={() => setConfirmBulk(true)}
        >
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          Delete {bulk.count} {bulk.count === 1 ? "priority" : "priorities"}
        </BulkButton>
      </BulkActionBar>

      {priorityQuery.isLoading && <PageLoader />}
      {priorityQuery.isError && (
        <SupportErrorState
          error={priorityQuery.error}
          resourceLabel="priorities"
        />
      )}

      {priorityQuery.data && (
        <Box component="ul" sx={{ display: "grid", gap: 1, listStyle: "none", m: 0, p: 0 }}>
          {rows.length > 0 && (
            <Box
              component="li"
              sx={{ display: "grid", gridTemplateColumns: PRIORITY_GRID, alignItems: "center", gap: 1.5, px: 1, fontSize: 12, color: "text.disabled" }}
            >
              <BulkCheckbox
                checked={bulk.allOnPageChecked}
                indeterminate={bulk.someOnPageChecked}
                onChange={(checked) => bulk.toggleAllOnPage(checked)}
                ariaLabel="Select all priorities"
              />
              <Box component="span" sx={{ gridColumn: "span 5" }}>Select all on page</Box>
            </Box>
          )}
          {rows.map((p) => (
            <PriorityRow
              key={p.id}
              priority={p}
              selected={bulk.isSelected(p.id)}
              onToggle={(checked, opts) => bulk.setChecked(p.id, checked, opts)}
              onSave={(body) => update.mutate({ id: p.id, body })}
              onDelete={() => setPendingDelete({ id: p.id, name: p.name })}
              isPending={update.isPending}
            />
          ))}
          {rows.length === 0 && (
            <Box component="li" sx={{ fontSize: 14, color: "text.disabled" }}>
              No priorities yet — use “Add priority” above to create one.
            </Box>
          )}
        </Box>
      )}

      <NewPriorityDialog
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={(b) =>
          create.mutate(b, { onSuccess: () => setShowCreate(false) })
        }
        isSubmitting={create.isPending}
      />

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Delete priority?"
        description={
          pendingDelete
            ? `"${pendingDelete.name}" will be deleted permanently. SLA policies bound to this priority will need to be re-pointed.`
            : ""
        }
        confirmLabel="Delete priority"
        onConfirm={() => {
          if (!pendingDelete) return;
          remove.mutate(pendingDelete.id);
        }}
      />

      <ConfirmDialog
        isOpen={confirmBulk}
        onClose={() => setConfirmBulk(false)}
        title={`Delete ${bulk.count} ${bulk.count === 1 ? "priority" : "priorities"}?`}
        description="SLA policies bound to any of these priorities will need to be re-pointed. This cannot be undone."
        confirmLabel={`Delete ${bulk.count} ${bulk.count === 1 ? "priority" : "priorities"}`}
        onConfirm={async () => {
          await bulkRemove.mutateAsync([...bulk.selected]);
          bulk.clear();
        }}
      />
    </Box>
  );
}
